// src/pages/Main/frontShot.js

import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import { withTheme } from 'context/theme';
import { FRONT_SHOTS } from 'pages/Main/frontShots';
import "pages/Main/frontShot.css";

const BASE = "/static/main/";

// Theme.DARK, written out: the string is also half of shots.json's `dest` contract.
const DARK = "dark";

/**
 * A screenshot's path for a theme: light is the bare name, dark takes `.dark`, and anything but "dark" is light.
 * Chosen from Theme.resolved() rather than a `<picture media>` source-set, which would follow the OS over an
 * explicit choice: docs/specs/2026-09-04-screenshot-library.md#the-landing-page-in-two-themes
 */
export function shotSrc(name, theme, ext = "png") {
  return `${BASE}${name}${DARK === theme ? ".dark" : ""}.${ext}`;
}

/** The entries of `shots` that have a picture: the view and the carousel index FRONT_SHOTS by name unchecked. */
export function shownShots(shots) {
  return shots.filter((shot) => FRONT_SHOTS[shot.name]);
}

/** A computed CSS length in pixels; one the browser cannot give (jsdom lays nothing out) counts as 0. */
export function cssLength(value) {
  const px = parseFloat(value);
  return Number.isFinite(px) ? px : 0;
}

/** Gives a picture's button the focus without scrolling: in a carousel's track that would scroll the clipped box. */
export function focusPicture(button) {
  if (button) {
    button.focus({ preventScroll: true });
  }
}

/** Whether the reader asked for reduced motion; false where there is no matchMedia (jsdom, old browsers). */
export function reducedMotion() {
  try {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  } catch (err) {
    return false;
  }
}

/**
 * The size a front screenshot is shown at full size: its capture size in CSS pixels, half the 2x file's own (an
 * SVG's viewBox stands in for it), scaled down only to fit `room`, never up.
 * @param shot a FRONT_SHOTS entry
 * @param room { width, height } the space it may take
 */
export function fitShot(shot, room) {
  const width = shot.width / 2;
  const height = shot.height / 2;
  const scale = Math.max(0, Math.min(1, room.width / width, room.height / height));
  return { width: Math.floor(width * scale), height: Math.floor(height * scale) };
}

/** The transform that lays a picture whose untransformed box is `box` over `thumb`, from their top-left corners. */
export function zoomTransform(box, thumb) {
  const scale = Math.max(thumb.width / box.width, thumb.height / box.height);
  return `translate(${thumb.left - box.left}px, ${thumb.top - box.top}px) scale(${scale})`;
}

// The clip of a picture shown whole; the one a clip opens to and closes from, since `none` does not interpolate.
export const UNCLIPPED = "inset(0px 0px 0px 0px)";

/** The `clip-path` that leaves, of a picture at `box`, only what its thumbnail shows, in the picture's own pixels. */
export function zoomClip(box, { box: whole, visible }) {
  const scale = whole.width / box.width;
  const px = (length) => `${Math.max(0, Math.round((length / scale) * 10) / 10)}px`;
  return `inset(${px(visible.top - whole.top)} ${px(whole.right - visible.right)} ${px(whole.bottom - visible.bottom)} `
    + `${px(visible.left - whole.left)})`;
}

function rect(left, top, width, height) {
  return { left, top, width, height, right: left + width, bottom: top + height };
}

function intersect(a, b) {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  return rect(left, top, Math.max(0, right - left), Math.max(0, bottom - top));
}

// One `object-position` coordinate: a percentage of the room left over, or a length.
function placed(value, room) {
  const amount = parseFloat(value);
  if (!Number.isFinite(amount)) {
    return room / 2;
  }
  return /%$/.test(value) ? (room * amount) / 100 : amount;
}

/**
 * A thumbnail's whole picture as laid out (`box`, a `cover` one spilling out) and the part left showing (`visible`).
 * Null when none of it is on screen or it cannot be measured.
 * @param aspect the picture's width over its height
 */
export function thumbView(element, aspect) {
  if (!element || !element.isConnected) {
    return null;
  }
  const own = element.getBoundingClientRect();
  if (!(own.width > 0 && own.height > 0)) {
    return null;
  }
  let box = rect(own.left, own.top, own.width, own.height);
  const style = window.getComputedStyle(element);
  if ("cover" === style.objectFit && aspect > 0) {
    const width = Math.max(own.width, own.height * aspect);
    const height = width / aspect;
    const [x, y] = (style.objectPosition || "50% 50%").split(/\s+/);
    box = rect(own.left + placed(x, own.width - width), own.top + placed(y, own.height - height), width, height);
  }
  let visible = rect(own.left, own.top, own.width, own.height);
  for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    if ("visible" !== (window.getComputedStyle(parent).overflow || "visible")) {
      const clip = parent.getBoundingClientRect();
      visible = intersect(visible, rect(clip.left, clip.top, clip.width, clip.height));
    }
  }
  const shown = intersect(visible, rect(0, 0, window.innerWidth, window.innerHeight));
  return shown.width > 0 && shown.height > 0 ? { box, visible } : null;
}

/** The longest of a computed `transition-duration` list, in ms; 0 where the browser gives none (jsdom). */
export function transitionMs(value) {
  return Math.max(0, ...String(value || "").split(",").map((part) => {
    const time = parseFloat(part);
    return Number.isFinite(time) ? time * (/ms\s*$/.test(part) ? 1 : 1000) : 0;
  }));
}

// The dialog element react-bootstrap's Modal renders inside `.modal`, in place of its centred `.modal-dialog` box:
// the view fills the viewport, so it takes the clicks a backdrop would, and decides itself which close it.
const ViewDialog = ({ className, contentClassName, children, ...rest }) => (
  <div className="fp-view-dialog" {...rest}>{children}</div>
);

/** One picture of `items` at full size, in the phases the spec's Motion names; mounted only while the dialog is. */
class ShotViewBody extends Component {
  constructor(props) {
    super(props);
    this.state = {
      room: { width: window.innerWidth, height: window.innerHeight },
      phase: reducedMotion() ? "open" : "start",
      from: null,
      fromClip: UNCLIPPED,
      to: null,
      toClip: UNCLIPPED,
      outgoing: null,
      incoming: false,
    };
    this.stage = React.createRef();
    this.figure = React.createRef();
    this.caption = React.createRef();
    this.picture = React.createRef();
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
    document.addEventListener("keydown", this.handleKey);
    this.hideThumb();
    this.measure(this.grow);
  }

  getSnapshotBeforeUpdate(prev) {
    const shown = "open" === this.state.phase || "opening" === this.state.phase;
    if (prev.index === this.props.index || !shown || reducedMotion()) {
      return null;
    }
    const picture = this.picture.current;
    return { src: picture.getAttribute("src"), box: picture.getBoundingClientRect() };
  }

  componentDidUpdate(prev, prevState, outgoing) {
    if (prev.index !== this.props.index) {
      this.measure();
      if (outgoing) {
        this.crossFade(outgoing);
      }
    }
    if (this.props.closing && !prev.closing) {
      this.shrink();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("keydown", this.handleKey);
    clearTimeout(this.ending);
    clearTimeout(this.opening);
    clearTimeout(this.fading);
    if (this.hidden) {
      this.hidden.removeAttribute("data-zoomed");
    }
  }

  // The thumbnail the view grew from stays in place but unseen while the view holds its picture, and with it its
  // frame: frontShot.css hides whatever frames a `data-zoomed` picture, so no empty card is left behind.
  hideThumb() {
    const thumb = this.props.getThumb ? this.props.getThumb(this.props.index) : null;
    if (thumb) {
      thumb.setAttribute("data-zoomed", "");
      this.hidden = thumb;
    }
  }

  // The room is the window less the stage's padding (the gutter), the caption and the gap above it, read from the
  // stylesheet as rendered. The caption wraps at a width of its own, so its height never depends on the picture.
  measure(then) {
    const stage = this.stage.current ? window.getComputedStyle(this.stage.current) : {};
    const gap = this.figure.current ? cssLength(window.getComputedStyle(this.figure.current).rowGap) : 0;
    const caption = this.caption.current ? this.caption.current.offsetHeight : 0;
    const room = {
      width: window.innerWidth - cssLength(stage.paddingLeft) - cssLength(stage.paddingRight),
      height: window.innerHeight - cssLength(stage.paddingTop) - cssLength(stage.paddingBottom) - caption - gap,
    };
    if (room.width !== this.state.room.width || room.height !== this.state.room.height) {
      this.setState({ room }, then);
    } else if (then) {
      then();
    }
  }

  handleResize = () => this.measure();

  // Where the picture sits untransformed, which is what a zoom starts from or returns to whatever moves it now:
  // the figure is never transformed, and offsets are layout, not paint. The offsets are the figure's only because
  // frontShot.css makes `.fp-view-figure` position: relative.
  pictureBox() {
    const figure = this.figure.current.getBoundingClientRect();
    const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = this.picture.current;
    return { left: figure.left + offsetLeft, top: figure.top + offsetTop, width: offsetWidth, height: offsetHeight };
  }

  // The thumbnail of the picture in view, as `thumbView` sees it now.
  thumbView() {
    const { getThumb, items, index } = this.props;
    const shot = FRONT_SHOTS[items[index].name];
    return thumbView(getThumb ? getThumb(index) : null, shot.width / shot.height);
  }

  // Makes the browser compute the style just committed, so the next change transitions from it.
  reflow() {
    this.picture.current.getBoundingClientRect();
  }

  // Lays the picture over its thumbnail, makes the browser compute that style, then lets it go: the transition
  // runs from the thumbnail to the final box. Called once the room, and so the final box, is settled.
  grow = () => {
    if ("start" !== this.state.phase) {
      return;
    }
    const view = this.thumbView();
    const box = this.pictureBox();
    const start = { from: view && zoomTransform(box, view.box), fromClip: view ? zoomClip(box, view) : UNCLIPPED };
    this.setState(start, () => {
      this.reflow();
      this.setState({ phase: "opening" }, () => {
        this.opening = setTimeout(this.opened, this.transitionMs() + 50);
      });
    });
  };

  opened = () => {
    clearTimeout(this.opening);
    if ("opening" === this.state.phase) {
      this.setState({ phase: "open" });
    }
  };

  transitionMs() {
    return transitionMs(window.getComputedStyle(this.picture.current).transitionDuration);
  }

  // Back onto the thumbnail where it is now, the page may have moved under the view; with none, a fade.
  shrink() {
    clearTimeout(this.opening);
    const view = this.thumbView();
    const box = this.pictureBox();
    const shrink = view && { phase: "shrink", to: zoomTransform(box, view.box), toClip: zoomClip(box, view) };
    this.setState(shrink || { phase: "fade" }, () => {
      this.ending = setTimeout(this.end, this.transitionMs() + 50);
    });
  }

  end = () => {
    if (!this.ended) {
      this.ended = true;
      clearTimeout(this.ending);
      this.props.onDone();
    }
  };

  // Only the picture's own transition of the property its phase moves ends it: a cross-fade's opacity still running
  // must not cut a shrink short.
  handleTransitionEnd = (event) => {
    const { phase, from } = this.state;
    const moves = { opening: from ? "transform" : "opacity", shrink: "transform", fade: "opacity" }[phase];
    if (event.target !== this.picture.current || event.propertyName !== moves) {
      return;
    }
    if ("opening" === phase) {
      this.opened();
    } else {
      this.end();
    }
  };

  // The picture that was shown stays where it was and fades out while the next fades in; no zoom per step.
  crossFade(outgoing) {
    clearTimeout(this.fading);
    this.setState({ outgoing, incoming: true }, () => {
      this.reflow();
      this.setState({ incoming: false }, () => {
        this.fading = setTimeout(() => this.setState({ outgoing: null }), this.transitionMs() + 50);
      });
    });
  }

  step = (delta) => {
    const { items, index, onIndex, closing } = this.props;
    if (!closing) {
      onIndex((index + delta + items.length) % items.length);
    }
  };

  handleKey = (event) => {
    if (this.props.items.length < 2) {
      return;
    }
    if ("ArrowLeft" === event.key || "ArrowRight" === event.key) {
      event.preventDefault();
      this.step("ArrowLeft" === event.key ? -1 : 1);
    }
  };

  // Every click closes except one on an arrow: the picture, the caption and the ground around them all do, and ✕
  // has nothing else to do.
  handleClick = (event) => {
    if (!event.target.closest(".fp-view-nav")) {
      this.props.onClose();
    }
  };

  // The picture is clipped to what its thumbnail shows at either end of a zoom, and open (UNCLIPPED) between them.
  pictureStyle() {
    const { phase, from, fromClip, to, toClip } = this.state;
    if (reducedMotion()) {
      return undefined;
    }
    if ("start" === phase && from) {
      return { transform: from, clipPath: fromClip };
    }
    return "shrink" === phase ? { transform: to, clipPath: toClip } : { clipPath: UNCLIPPED };
  }

  render() {
    const { items, index, theme } = this.props;
    const { phase, from, outgoing, incoming } = this.state;
    const item = items[index];
    const shot = FRONT_SHOTS[item.name];
    const size = fitShot(shot, this.state.room);
    const several = items.length > 1;
    // Transitions run only while something moves; never while the start is laid, where the picture must land on
    // its thumbnail rather than travel there.
    const moving = !reducedMotion() && (["opening", "shrink", "fade"].includes(phase) || !!outgoing);
    const classes = ["fp-view-stage", `fp-view-${phase}`, moving ? "fp-view-moving" : "",
      "start" === phase && !from ? "fp-view-from-nothing" : ""].filter(Boolean).join(" ");
    return (
      <div className={classes} ref={this.stage} onClick={this.handleClick}>
        <div className="fp-view-scrim" />
        <button type="button" className="fp-view-close" aria-label="Close">&times;</button>
        {several && (
          <button type="button" className="fp-view-nav fp-view-prev" aria-label="Previous picture"
            onClick={() => this.step(-1)}>&lsaquo;</button>
        )}
        {outgoing && (
          <img className={`fp-view-outgoing${incoming ? "" : " fp-view-gone"}`} src={outgoing.src} alt=""
            aria-hidden="true" style={{ left: outgoing.box.left, top: outgoing.box.top, width: outgoing.box.width,
              height: outgoing.box.height }} />
        )}
        <figure className="fp-view-figure" ref={this.figure}>
          <img className={`fp-view-picture${incoming ? " fp-view-incoming" : ""}`} ref={this.picture}
            src={shotSrc(`front/${item.name}`, theme, shot.ext)} width={size.width} height={size.height}
            alt={item.alt} style={this.pictureStyle()} onTransitionEnd={this.handleTransitionEnd} />
          {/* The alt is the caption; the picture already carries it to a screen reader. */}
          <figcaption className="fp-view-caption" ref={this.caption} aria-hidden="true">{item.alt}</figcaption>
        </figure>
        {several && (
          <button type="button" className="fp-view-nav fp-view-next" aria-label="Next picture"
            onClick={() => this.step(1)}>&rsaquo;</button>
        )}
      </div>
    );
  }
}

/**
 * The full-size view of front screenshots, on react-bootstrap's Modal, which brings the focus trap, Esc and the
 * scroll lock; the view draws its own scrim and runs its own motion, so the Modal has neither backdrop nor fade.
 * Spec: docs/specs/2026-10-17-front-carousel-lightbox.md#1--full-size-view
 * @param items [{ name, alt }], each with a FRONT_SHOTS entry (shownShots)
 * @param index the item shown
 * @param onIndex (index) => void, asked for another item by ← → or an arrow
 * @param getThumb (index) => the thumbnail `<img>` that item grows from and shrinks back to, or null for none
 * @param onClosed () => void, once the view has gone: where the focus returns is the opener's to say, because a
 *   carousel closing on another slide than it opened from has hidden the picture that opened it.
 */
export class ShotView extends Component {
  constructor(props) {
    super(props);
    this.state = { closing: false };
  }

  componentDidUpdate(prev) {
    // The Modal has let go of the focus by the next task.
    if (prev.show && !this.props.show) {
      this.closing = setTimeout(this.closed);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.closing);
  }

  // A second close while closing changes nothing: the body starts its shrink only on the edge into `closing`.
  close = () => {
    if (reducedMotion()) {
      this.props.onHide();
    } else {
      this.setState({ closing: true });
    }
  };

  done = () => {
    this.setState({ closing: false });
    this.props.onHide();
  };

  closed = () => {
    if (this.props.onClosed) {
      this.props.onClosed();
    }
  };

  render() {
    const { show, items, index, onIndex, getThumb, theme } = this.props;
    return (
      <Modal show={show} onHide={this.close} animation={false} backdrop={false} restoreFocus={false}
        dialogAs={ViewDialog} className="fp-view" aria-label="Picture at full size">
        <ShotViewBody items={items} index={index} onIndex={onIndex} getThumb={getThumb} theme={theme}
          closing={this.state.closing} onClose={this.close} onDone={this.done} />
      </Modal>
    );
  }
}

export const ThemedShotView = withTheme(ShotView);

/**
 * A front-page screenshot that follows the theme: one `<img>` from the pair in public/static/main/front/.
 * An unknown name renders nothing rather than a broken image.
 * @param loading "lazy" by default; the hero composition, on screen at load, passes "eager".
 * @param zoomable false inside a card that is itself a link, where a click keeps navigating; otherwise the picture
 *   is a button that opens it at full size.
 * @param onOpen given by a carousel, which opens its own view of all its pictures; without it the shot opens its own.
 * @param tabIndex the button's, for a carousel's hidden slides.
 * @param fixedTheme a theme the picture always shows, here and at full size, whatever the page's.
 */
export class FrontShot extends Component {
  constructor(props) {
    super(props);
    this.state = { open: false };
    this.button = React.createRef();
  }

  open = (event) => {
    // Safari does not focus a clicked button; the focus is where a reader returns to after the view.
    event.currentTarget.focus();
    if (this.props.onOpen) {
      this.props.onOpen();
    } else {
      this.setState({ open: true });
    }
  };

  render() {
    // `themeChoice` is injected by withTheme and is not a DOM attribute; only `theme` is wanted.
    const { name, alt, className, loading = "lazy", zoomable = true, onOpen, tabIndex, fixedTheme } = this.props;
    const theme = fixedTheme || this.props.theme;
    const shot = FRONT_SHOTS[name];
    if (!shot) {
      return null;
    }
    // A carousel's slide is swiped, so its picture must not start the browser's own image drag.
    const img = (
      <img className={className} src={shotSrc(`front/${name}`, theme, shot.ext)} width={shot.width}
           height={shot.height} alt={alt} loading={loading} decoding="async"
           draggable={onOpen ? false : undefined} />
    );
    if (!zoomable) {
      return img;
    }
    return (
      <>
        <button type="button" className="fp-zoom" ref={this.button} aria-label={`Open the picture at full size: ${alt}`}
          onClick={this.open} tabIndex={tabIndex}>{img}</button>
        {!onOpen && (
          <ShotView show={this.state.open} items={[{ name, alt }]} index={0} onIndex={() => {}}
            onHide={() => this.setState({ open: false })} onClosed={() => focusPicture(this.button.current)}
            getThumb={() => this.button.current && this.button.current.querySelector("img")} theme={theme} />
        )}
      </>
    );
  }
}

export default withTheme(FrontShot);
